import { Stack } from '@mui/material';
import { FilterChip, RemoveAllFiltersChip } from '@retail/components';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useFilters } from '@retail/products/hooks';
import { useProductsTFunction } from '@retail/products/i18n';
import { GroupOption, IncludesOrMissing, ProductGroup } from '@retail/products/types';
import {
  DocumentationDataKey,
  DocumentationFilterDataKey,
  documentationTextMap,
  EnvironmentDataFilterKey,
  environmentFilterDataTextMap,
  insightFilters,
  InsightFilters,
  PictureDataKey,
  PictureFilterDataKey,
  pictureValueTextMap,
} from '@retail/products/utils';
import { SupplierBase } from '@retail/suppliers/types';
import { useCallback } from 'react';

interface Props {
  selectedSupplier: SupplierBase | null;
  selectedProductGroup: ProductGroup | null;
  searchQuery: string | null;
  inAssortment: GroupOption;
  assortmentTags?: AssortmentTag[];
  includeNotIncludedItemData: boolean;
  environmentValuesSelected: EnvironmentDataFilterKey[];
  documentationValuesSelected: DocumentationFilterDataKey[];
  pictureValuesSelected: PictureFilterDataKey[];
  climateDataA1ToA3Included: boolean;
  includedOrMissingFieldValue: IncludesOrMissing;

  toggleIncludeNotIncludedItemData: () => void;
  updateEnvironmentFilterValues: (value: EnvironmentDataFilterKey) => void;
  updateDocumentationFilterValues: (value: DocumentationFilterDataKey) => void;
  updatePictureFilterValues: (value: PictureFilterDataKey) => void;
  toggleClimateDataA1ToA3Included: () => void;
  toggleIncludedOrMissingFieldValue: (includedOrMissing: IncludesOrMissing) => void;
  resetFilters: () => void;
}

export function InsightReportFilterChips({
  searchQuery,
  selectedSupplier,
  selectedProductGroup,
  inAssortment,
  assortmentTags,
  includeNotIncludedItemData,
  environmentValuesSelected,
  documentationValuesSelected,
  pictureValuesSelected,
  climateDataA1ToA3Included,
  includedOrMissingFieldValue,
  toggleIncludeNotIncludedItemData,
  updateEnvironmentFilterValues,
  updateDocumentationFilterValues,
  updatePictureFilterValues,
  toggleClimateDataA1ToA3Included,
  resetFilters,
}: Props) {
  const t = useProductsTFunction();
  const { setFilters, updateFilter } = useFilters<InsightFilters>(insightFilters);

  const getFieldText = useCallback(
    (field: string) =>
      `${includedOrMissingFieldValue === 'INCLUDES'
        ? t('products.mgItem.supplierItem.fieldExists')
        : t('products.mgItem.supplierItem.fieldMissing')
      }: ${field}`,
    [includedOrMissingFieldValue, t]
  );
  return (
    <Stack flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
      {includeNotIncludedItemData && (
        <FilterChip
          name={t('products.mgItem.showUnincludedData')}
          onDelete={toggleIncludeNotIncludedItemData}
        />
      )}
      {selectedSupplier && (
        <FilterChip
          name={selectedSupplier.name}
          onDelete={() => setFilters({ 'supplierNumber': undefined })}
        />
      )}
      {!!selectedProductGroup && (
        <FilterChip
          key={selectedProductGroup.groupNumber}
          name={`${selectedProductGroup.groupNumber} ${selectedProductGroup.name}`}
          onDelete={() => setFilters({ 'productGroupNumber': undefined })}
        />
      )}
      {inAssortment === 'YES' && <FilterChip
        name={t('products.mgItem.supplierItem.inAssortment')}
        onDelete={() =>
          setFilters({ 'inAssortment': undefined })
        }
      />}
      {assortmentTags && assortmentTags.map((tag) => {
        return (
          <FilterChip
            key={tag.id}
            name={tag.name}
            onDelete={() => updateFilter('subAssortmentIds', tag.id.toString())}
          />
        );
      })}
      {searchQuery && (
        <FilterChip
          name={searchQuery}
          onDelete={() => setFilters({ 'searchQuery': undefined })}
        />
      )}
      {!!environmentValuesSelected.length &&
        environmentValuesSelected.map((field) => {
          return (
            <FilterChip
              key={field}
              name={getFieldText(
                t(`${environmentFilterDataTextMap[field as EnvironmentDataFilterKey]}`)
              )}
              onDelete={() => updateEnvironmentFilterValues(field)}
            />
          );
        })}
      {!!documentationValuesSelected.length &&
        documentationValuesSelected.map((field) => {
          return (
            <FilterChip
              key={field}
              name={getFieldText(t(`${documentationTextMap[field as DocumentationDataKey]}`))}
              onDelete={() => updateDocumentationFilterValues(field)}
            />
          );
        })}
      {!!pictureValuesSelected.length &&
        pictureValuesSelected.map((field) => {
          return (
            <FilterChip
              key={field}
              name={getFieldText(t(`${pictureValueTextMap[field as PictureDataKey]}`))}
              onDelete={() => updatePictureFilterValues(field)}
            />
          );
        })}
      {climateDataA1ToA3Included && (
        <FilterChip
          name={t('products.mgItem.gwp.A1ToA3')}
          onDelete={toggleClimateDataA1ToA3Included}
        />
      )}
      {(searchQuery ||
        !!selectedProductGroup ||
        !!selectedSupplier ||
        inAssortment === 'YES' ||
        includeNotIncludedItemData ||
        !!climateDataA1ToA3Included ||
        !!environmentValuesSelected.length ||
        !!documentationValuesSelected.length ||
        !!assortmentTags?.length ||
        !!pictureValuesSelected.length) && <RemoveAllFiltersChip resetFilters={resetFilters} />}
    </Stack>
  );
}
